import { z } from 'zod';
import { MEMBER_STATUSES } from '../../ontology/objects/member.ts';
import { stepSchema } from '../../ontology/objects/path.ts';
import { instant } from './primitives.ts';

/** Only what the person holding the link may learn. */
export const inviteLandingSchema = z.object({
  invite: z
    .object({
      name: z.string(),
      picture: z.string().nullable(),
      vouches: z.boolean(),
      admits: z.boolean(),
    })
    .nullable(),
});

export type InviteLanding = z.infer<typeof inviteLandingSchema>;

const pathSchema = z.object({
  steps: z.array(
    z.object({
      step: stepSchema,
      reached: z.boolean(),
      title: z.string(),
      why: z.string(),
      action: z.string(),
      href: z.string(),
    }),
  ),
  friendsDone: z.number().int().nonnegative(),
  /** The path is done, so the member's invite lets a friend straight in. */
  admits: z.boolean(),
});

export type PathRead = z.infer<typeof pathSchema>;

export const invitesSchema = z.object({
  link: z.string(),
  allowance: z.number().int().nonnegative(),
  left: z.number().int().nonnegative(),
  invited: z.array(
    z.object({
      name: z.string(),
      picture: z.string().nullable(),
      status: z.enum(MEMBER_STATUSES),
      at: instant,
    }),
  ),
  path: pathSchema,
  pitch: z.string(),
});

export type Invites = z.infer<typeof invitesSchema>;

/** What the person holding a follow invite may learn: whose it is, and whether it still lets them in. */
export const followInviteLandingSchema = z.object({
  join: z
    .object({
      analystId: z.number().int().positive(),
      name: z.string(),
      bio: z.string().nullable(),
      picture: z.string().nullable(),
      /** Whose it is: only a Google sign-in as this address takes it. */
      email: z.string().nullable(),
      /** False once used, withdrawn or expired, or the analyst no longer publishes. */
      open: z.boolean(),
    })
    .nullable(),
});

export type FollowInviteLanding = z.infer<typeof followInviteLandingSchema>;
